import { formatDate } from "@angular/common";

const DEFAULT_LOCALE = "en-SG";
const DEFAULT_DATE_TIME_FORMAT = "dd/MM/yyyy HH:mm:ss";

/**
 * Formats a date/time string into the browser's local time display.
 *
 * Example input/output:
 * - "2026-03-31T01:54:22.906884984Z" -> "31/03/2026 09:54:22" in UTC+08:00
 * - null -> "-"
 * - "not-a-date" -> "not-a-date"
 */
export function formatLocalDateTime(
  value: string | null | undefined,
  options?: {
    fallback?: string;
    format?: string;
    locale?: string;
  },
): string {
  const fallback = options?.fallback ?? "-";

  if (!value) {
    return fallback;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return formatDate(
    parsedDate,
    options?.format ?? DEFAULT_DATE_TIME_FORMAT,
    options?.locale ?? DEFAULT_LOCALE,
  );
}

/**
 * F4: midnight on the first day of the reference month (local time).
 * Default "Date From" for eSubmission report/enquiry filters.
 * Legacy equivalent: DateUtil.getFirstDayOfTheMonth(...).
 */
export function firstDayOfCurrentMonth(reference: Date = new Date()): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), 1);
}

/**
 * F4: the reference date at midnight (local time). Default "Date To" = today.
 */
export function todayDate(reference: Date = new Date()): Date {
  return new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
  );
}

/**
 * F4: default report/enquiry date range — From = first day of the current month, To = today.
 * Mirrors the legacy criteria init (start = first day of month, end = today).
 */
export function defaultReportDateRange(reference: Date = new Date()): {
  from: Date;
  to: Date;
} {
  return {
    from: firstDayOfCurrentMonth(reference),
    to: todayDate(reference),
  };
}

/**
 * F4: normalizes a date value to local midnight (date-only), or null if unparseable.
 * Keeps date-range comparisons timezone-stable when controls hold Date objects.
 */
export function startOfDay(value: string | number | Date): Date | null {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * F4: adds `months` calendar months, clamping day overflow (e.g. 31 Jan + 1 month => 28/29 Feb).
 * Used by the date-range span check (max span = RPT_FILTER_PERIOD months).
 */
export function addMonths(date: Date, months: number): Date {
  const day = date.getDate();
  const result = new Date(date.getFullYear(), date.getMonth() + months, day);
  // If the target month has fewer days, the day rolled into the next month — pull back to month end.
  if (result.getDate() !== day) {
    result.setDate(0);
  }
  return result;
}
